import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { computePostOpDay } from "../lib/date";
import {
  Sparkle, Pill, Drop, BowlFood, Snowflake, PersonSimpleRun, Cigarette, Bandaids, Heart,
  CheckCircle, Pulse, Warning, CloudArrowUp, ImageSquare, Star, Prescription,
} from "@phosphor-icons/react";

const INSTR_META = {
  farmaci: { label: "Farmaci", Icon: Pill },
  igiene_orale: { label: "Igiene orale", Icon: Drop },
  alimentazione: { label: "Alimentazione", Icon: BowlFood },
  ghiaccio: { label: "Ghiaccio", Icon: Snowflake },
  attivita_fisica: { label: "Attività fisica", Icon: PersonSimpleRun },
  fumo: { label: "Fumo", Icon: Cigarette },
  sanguinamento: { label: "Sanguinamento", Icon: Bandaids },
  edema: { label: "Edema / gonfiore", Icon: Heart },
};
const INSTR_ORDER = ["farmaci", "igiene_orale", "alimentazione", "ghiaccio", "attivita_fisica", "fumo", "sanguinamento", "edema"];

const SWELLING = [
  { v: "none", l: "Assente" },
  { v: "mild", l: "Lieve" },
  { v: "moderate", l: "Moderato" },
  { v: "severe", l: "Importante" },
];
const BLEEDING = [
  { v: "none", l: "Assente" },
  { v: "mild", l: "Lieve" },
  { v: "persistent", l: "Persistente" },
];
const MOUTH = [
  { v: "none", l: "Nessuna" },
  { v: "mild", l: "Lieve" },
  { v: "severe", l: "Importante" },
];

const RISK_META = {
  green: { label: "Decorso regolare", color: "bg-[#EAFBEF] text-[#1F8B4C] border-[#1F8B4C]/40", Icon: CheckCircle },
  yellow: { label: "Da monitorare", color: "bg-[#FFFAE9] text-[#8A6D1F] border-[#8A6D1F]/40", Icon: Pulse },
  red: { label: "Contatta lo studio", color: "bg-[#FAEBEB] text-[#A84A4A] border-[#A84A4A]/40", Icon: Warning },
};

const TIMELINE_DAYS = [1, 3, 7, 14, 30];

export default function PortalAssistant({ patientFirstName, patientToken, followupId }) {
  const [init, setInit] = useState(null);
  const [day0, setDay0] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState({
    pain: 0,
    swelling: "none",
    bleeding: "none",
    fever: false,
    pus: false,
    smoked_today: false,
    cigarettes_today: 0,
    mouth_opening: "none",
    temperature: "",
    breathing_difficulty: false,
    swallowing_difficulty: false,
    medication_taken: true,
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [checkIn, setCheckIn] = useState(null);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(0);

  const load = async () => {
    // If we have no patient token yet, just stay in loading state — the parent
    // controls the source of truth. Never show "Token non valido" here:
    // the parent (PatientPortal) is the only place that decides validity.
    if (!patientToken) {
      setLoading(true);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const cfg = { headers: { Authorization: `Bearer ${patientToken}` } };
      const assistantParams = followupId ? { params: { followup_id: followupId }, ...cfg } : cfg;
      const [initRes, day0Res] = await Promise.all([
        api.get(`/portal/assistant`, assistantParams),
        api.get(`/portal/day0`, cfg).catch(() => ({ data: null })),
      ]);
      setInit(initRes.data);
      setDay0(day0Res?.data || null);
      if (initRes.data.last_checkin) {
        const ai = initRes.data.last_checkin.ai || {};
        const risk = ai.risk === "normal" ? "green" : ai.risk === "monitor" ? "yellow" : ai.risk === "urgent" || ai.risk === "contact_clinic" ? "red" : null;
        if (risk) setCheckIn({ risk, warm_response: ai.whatsapp_message || "", suggested_next_step: "" });
      }
      // Pre-fill smoker flag in check-in from confirmed profile
      const sp = initRes.data?.smoking_profile;
      if (sp?.is_smoker) {
        setQ((s) => ({ ...s, smoked_today: !!sp.smoked_24h }));
      }
    } catch (e) {
      // Never expose backend auth errors here — parent already validated identity.
      // We just keep a non-blocking soft state.
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        setError("");
      } else {
        setError(formatApiError(e?.response?.data?.detail) || "Assistente temporaneamente non disponibile.");
      }
    } finally { setLoading(false); }
  };

  // Reload whenever the patient token or followup_id changes
  useEffect(() => { load(); }, [patientToken, followupId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!patientToken) return;
    setSubmitting(true);
    setError("");
    try {
      const payload = { ...q };
      payload.temperature = q.fever && q.temperature !== "" ? parseFloat(q.temperature) : null;
      // Tie the response to the exact scheduled_followup the email referenced.
      // Prefer the followup_id surfaced by the backend (resolved + validated),
      // fall back to the URL param the patient arrived with.
      const tiedId = init?.followup_id || followupId || null;
      if (tiedId) payload.followup_id = tiedId;
      const { data } = await api.post(`/portal/check-in`, payload, { headers: { Authorization: `Bearer ${patientToken}` } });
      setCheckIn(data);
      // refresh init to update review_eligible & last_checkin
      load();
      // smooth scroll to result
      setTimeout(() => document.getElementById("assistant-response")?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        setError("La sessione è scaduta. Riapri il link che hai ricevuto.");
      } else {
        setError(formatApiError(e?.response?.data?.detail) || "Errore invio. Riprova.");
      }
    } finally { setSubmitting(false); }
  };

  const pickPhoto = () => fileInputRef.current?.click();
  const onPhoto = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    if (!patientToken) return;
    setUploading(true);
    try {
      for (const f of list) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("category", "photo");
        await api.post(`/portal/photo`, fd, { headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${patientToken}` } });
      }
      setUploaded((n) => n + list.length);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        setError("La sessione è scaduta. Riapri il link che hai ricevuto.");
      } else {
        setError(formatApiError(err?.response?.data?.detail) || "Errore caricamento foto.");
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  if (loading) {
    return (
      <div className="elev-card p-6 text-center text-[#56635D]" data-testid="assistant-loading">
        <Sparkle size={20} weight="duotone" className="text-brand-700 mx-auto mb-2" /> Sto preparando il tuo piano del giorno…
      </div>
    );
  }

  // Compute post-op day independently: prefer assistant briefing, else derive
  // from day0 intervention_date (Europe/Rome calendar day), else fall back to 0.
  const computeDay = () => {
    if (init?.post_op_day !== undefined && init?.post_op_day !== null) return init.post_op_day;
    return computePostOpDay(day0?.intervention_date);
  };
  const day = computeDay();
  // Show the Day 0 briefing card ONLY when the patient is genuinely at Day 0.
  // If the computed post-op day is > 0 (whether from ?followup_id= or from
  // the Europe/Rome surgery_date fallback), the patient must see that day's
  // instructions instead of being detoured through the initial briefing.
  // This also fixes the case where an older email (without followup_id) is
  // clicked days after surgery: the portal shows Day N, not the Day 0 card.
  const fromFollowupEmail = Boolean(followupId && day > 0);
  const showDay0 = day0 && !day0.acknowledged && day === 0;
  const briefingAvailable = !!init;
  // Debug panel: visible when URL contains ?debug=1 (preview troubleshooting only)
  const debugMode = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("debug") === "1";
  const fallbackDay = computePostOpDay(day0?.intervention_date);

  return (
    <div className="space-y-6" data-testid="portal-assistant">
      {debugMode && (
        <div className="elev-card p-4 bg-yellow-50 border-2 border-yellow-400 text-xs font-mono text-[#1C2321]" data-testid="debug-panel">
          <div className="font-bold mb-2 text-yellow-800">⚠ DEBUG FOLLOWUP (only when ?debug=1 in URL)</div>
          <div>1. token (preview): {patientToken ? patientToken.slice(0, 32) + "…" : "(none)"}</div>
          <div>2. followup_id query: {String(followupId || "(MISSING)")}</div>
          <div>3. followup_id received by frontend: {String(followupId || "(missing)")}</div>
          <div>4. followup_id validated by backend: {String(init?.followup_id || "(null)")}</div>
          <div>5. followup_day resolved (from DB): {String(init?.post_op_day)} (due_date={String(init?.followup_due_date || "-")})</div>
          <div>6. fallback_day (intervention_date Europe/Rome): {String(fallbackDay)}</div>
          <div>7. final_day_used (passed to PortalAssistant): <b>{String(day)}</b></div>
          <div>8. day passed to "Le istruzioni di oggi": <b>{String(day)}</b> (same as #7 — TodayInstructions receives `day` prop unchanged)</div>
          <div>9. source: <b>{init?.day_source === "followup_id" ? "followup_id ✓" : "fallback_surgery_date"}</b></div>
          <div>10. fromFollowupEmail: {String(fromFollowupEmail)} | showDay0: {String(showDay0)} | day0.ack: {String(day0?.acknowledged)}</div>
        </div>
      )}
      {/* Greeting */}
      <div className="elev-card p-6 bg-brand-50 border-brand-200" data-testid="assistant-greeting">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-brand-700 text-white flex items-center justify-center shrink-0">
            <Sparkle size={20} weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-[0.2em] text-brand-400">Assistente Post-Operatorio Mediadent</div>
            <h2 className="font-heading text-xl font-medium text-[#1C2321] mt-1 leading-snug">
              {(init && init.greeting) || `Ciao ${patientFirstName}, sono l'assistente post-operatorio Mediadent. Ti aiuto a seguire correttamente le indicazioni dopo il tuo intervento.`}
            </h2>
            <div className="mt-3 inline-flex items-center gap-2 text-xs font-mono px-2.5 py-1 rounded-full bg-white border border-brand-200 text-brand-700">
              Giorno post-operatorio: {day}
            </div>
          </div>
        </div>
      </div>

      {/* Day 0 Card — must be acknowledged before everything else */}
      {showDay0 && (
        <Day0Card day0={day0} patientToken={patientToken} onAck={load} />
      )}

      {/* Citisina protocol (visible if smoker confirmed) */}
      {!showDay0 && day0?.citisina_protocol && day0?.smoking_profile?.is_smoker && (
        <CitisinaProtocolCard protocol={day0.citisina_protocol} />
      )}

      {/* Timeline — only if briefing available */}
      {briefingAvailable && <Timeline day={day} />}

      {/* Le istruzioni di oggi — SEMPRE visibile (con fallback se briefing fallisce) */}
      {!showDay0 && (
        <TodayInstructions
          init={init}
          day={day}
          smokingProfile={day0?.smoking_profile}
          citisina={day0?.citisina_protocol}
          patientFirstName={patientFirstName}
          patientToken={patientToken}
          onAck={load}
        />
      )}

      {/* Section 2: Come stai? */}
      <form onSubmit={submit} className="elev-card p-6 space-y-5" data-testid="assistant-questionnaire">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-brand-400 mb-1">Sezione 2</div>
          <h3 className="font-heading text-xl font-medium text-[#1C2321]">Come stai oggi?</h3>
          <p className="text-sm text-[#56635D] mt-1">Le tue risposte aiutano lo studio a monitorare la guarigione.</p>
        </div>

        <SliderRow label={`Dolore (0-10): ${q.pain}`} value={q.pain} onChange={(v) => setQ((s) => ({ ...s, pain: v }))} testId="q-pain" />

        <RadioRow label="Gonfiore" value={q.swelling} options={SWELLING} onChange={(v) => setQ((s) => ({ ...s, swelling: v }))} testId="q-swelling" />
        <RadioRow label="Sanguinamento" value={q.bleeding} options={BLEEDING} onChange={(v) => setQ((s) => ({ ...s, bleeding: v }))} testId="q-bleeding" />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Toggle label="Febbre" checked={q.fever} onChange={(v) => setQ((s) => ({ ...s, fever: v }))} testId="q-fever" />
          <Toggle label="Pus o cattivo sapore" checked={q.pus} onChange={(v) => setQ((s) => ({ ...s, pus: v }))} testId="q-pus" />
          <Toggle label="Hai fumato oggi" checked={q.smoked_today} onChange={(v) => setQ((s) => ({ ...s, smoked_today: v, cigarettes_today: v ? s.cigarettes_today : 0 }))} testId="q-smoke" />
        </div>

        {q.smoked_today && (
          <div data-testid="q-cigarettes-wrapper">
            <label className="block text-xs uppercase tracking-[0.18em] text-[#56635D] mb-2">
              Quante sigarette hai fumato oggi: <span className="font-mono text-brand-700">{q.cigarettes_today}</span>
            </label>
            <input
              type="range" min={0} max={40} value={q.cigarettes_today}
              onChange={(e) => setQ((s) => ({ ...s, cigarettes_today: parseInt(e.target.value, 10) }))}
              data-testid="q-cigarettes"
              className="w-full accent-brand-700"
            />
            <div className="flex justify-between text-[10px] text-[#56635D] mt-1 px-0.5"><span>0</span><span>10</span><span>20+</span></div>
            {q.cigarettes_today > 10 && (
              <div className="mt-2 text-xs text-[#A84A4A] inline-flex items-center gap-1.5"><Warning size={12} weight="duotone" /> Oltre 10 sigarette/die: rallenta seriamente la guarigione.</div>
            )}
          </div>
        )}

        <RadioRow label="Difficoltà ad aprire la bocca" value={q.mouth_opening} options={MOUTH} onChange={(v) => setQ((s) => ({ ...s, mouth_opening: v }))} testId="q-mouth" />

        {q.fever && (
          <div data-testid="q-temperature-wrapper">
            <label className="block text-xs uppercase tracking-[0.18em] text-[#56635D] mb-2">Temperatura (°C)</label>
            <input type="number" step="0.1" min="35" max="42" value={q.temperature}
              onChange={(e) => setQ((s) => ({ ...s, temperature: e.target.value }))}
              placeholder="es. 38.2" data-testid="q-temperature"
              className="w-full px-3 py-2.5 border border-[#E6E8E6] rounded-lg bg-white focus:border-brand-700" />
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Toggle label="Difficoltà a respirare" checked={q.breathing_difficulty} onChange={(v) => setQ((s) => ({ ...s, breathing_difficulty: v }))} testId="q-breathing" />
          <Toggle label="Difficoltà a deglutire" checked={q.swallowing_difficulty} onChange={(v) => setQ((s) => ({ ...s, swallowing_difficulty: v }))} testId="q-swallowing" />
        </div>

        <Toggle label="Sto assumendo i farmaci come prescritto" checked={q.medication_taken} onChange={(v) => setQ((s) => ({ ...s, medication_taken: v }))} testId="q-medication" />

        <div>
          <label className="block text-xs uppercase tracking-[0.18em] text-[#56635D] mb-2">Note (facoltative)</label>
          <textarea rows={2} value={q.notes}
            onChange={(e) => setQ((s) => ({ ...s, notes: e.target.value }))}
            placeholder="Aggiungi qui qualsiasi informazione utile..."
            data-testid="q-notes"
            className="w-full px-3 py-2.5 border border-[#E6E8E6] rounded-lg bg-white focus:border-brand-700" />
        </div>

        {error && <div className="text-sm text-[#A84A4A] bg-[#A84A4A]/10 rounded-lg px-3 py-2">{error}</div>}

        <button type="submit" disabled={submitting} data-testid="assistant-submit" className="inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-5 py-3 rounded-lg disabled:opacity-60">
          {submitting ? "Invio in corso…" : "Invia all'assistente"}
        </button>
      </form>

      {/* Section 3 & 4: Risk + warm response */}
      {checkIn && (
        <div id="assistant-response" className="space-y-4 fade-up" data-testid="assistant-response">
          <RiskCard risk={checkIn.risk} />

          <div className="elev-card p-6" data-testid="assistant-warm-response">
            <div className="text-xs uppercase tracking-[0.2em] text-brand-400 mb-2 flex items-center gap-2">
              <Sparkle size={14} weight="duotone" /> Risposta dell&apos;assistente
            </div>
            <p className="text-[#1C2321] leading-relaxed whitespace-pre-line">{checkIn.warm_response}</p>
            {checkIn.suggested_next_step && (
              <div className="mt-4 p-3 rounded-lg bg-brand-50 border border-brand-200">
                <div className="text-xs uppercase tracking-[0.18em] text-brand-700 mb-1">Prossimo passo</div>
                <div className="text-sm text-[#1C2321]">{checkIn.suggested_next_step}</div>
              </div>
            )}
          </div>

          {/* Section 8: review request */}
          {checkIn.risk === "green" && day > 14 && (
            <div className="elev-card p-6 bg-brand-50 border-brand-200" data-testid="assistant-review-request">
              <div className="text-xs uppercase tracking-[0.2em] text-brand-400 mb-1 flex items-center gap-1.5"><Star size={14} weight="duotone" /> Un piccolo favore</div>
              <h3 className="font-heading text-xl font-medium text-[#1C2321]">Sei soddisfatto delle cure ricevute?</h3>
              <p className="text-sm text-[#56635D] mt-2">La tua opinione aiuta altri pazienti a scegliere con fiducia il nostro studio.</p>
              <a
                href={(init && init.google_review_url) || "https://search.google.com/local/writereview?placeid=Mediadent+Sarzana"}
                target="_blank" rel="noreferrer"
                data-testid="assistant-review-button"
                className="mt-4 inline-flex items-center gap-2 bg-[#4285F4] hover:bg-[#3367D6] text-white px-4 py-2.5 rounded-lg text-sm"
              >
                <Star size={16} weight="fill" /> Lascia una recensione Google
              </a>
            </div>
          )}
        </div>
      )}

      {/* Section 5: Photo upload */}
      <div className="elev-card p-6" data-testid="assistant-photo-upload">
        <div className="text-xs uppercase tracking-[0.2em] text-brand-400 mb-1">Sezione 5</div>
        <h3 className="font-heading text-xl font-medium text-[#1C2321]">Carica una foto per lo studio</h3>
        <p className="text-sm text-[#56635D] mt-1">Foto intraorale, foto del volto/gonfiore o foto del sito chirurgico. Saranno visibili solo allo studio. Max 5 foto al giorno, 10MB per foto.</p>
        <input ref={fileInputRef} type="file" accept="image/*" capture="environment" multiple onChange={onPhoto} className="hidden" data-testid="assistant-photo-input" />
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button onClick={pickPhoto} disabled={uploading} data-testid="assistant-photo-button" className="inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-4 py-2.5 rounded-lg disabled:opacity-60">
            <CloudArrowUp size={16} weight="duotone" /> {uploading ? "Caricamento…" : "Scegli o scatta foto"}
          </button>
          {uploaded > 0 && (
            <div className="text-sm text-brand-700 inline-flex items-center gap-1.5">
              <CheckCircle size={14} weight="duotone" /> {uploaded} {uploaded === 1 ? "foto caricata" : "foto caricate"}
            </div>
          )}
        </div>
        <div className="mt-3 text-[11px] text-[#56635D] flex items-center gap-1.5"><ImageSquare size={12} weight="duotone" /> Max 10MB per immagine. Massimo 5 foto al giorno.</div>
      </div>
    </div>
  );
}

// ---------- Today's Instructions (always visible, with local fallback) ----------
const STATIC_FALLBACK = {
  0: {
    focus: "Oggi è il giorno dell'intervento: la priorità è proteggere il coagulo, ridurre il gonfiore e iniziare correttamente la terapia.",
    farmaci: "Assumi tutti i farmaci prescritti dal tuo dentista rispettando gli orari. Non saltare l'antibiotico.",
    igiene_orale: "Non sciacquare la bocca per le prime 24 ore. Niente spazzolino sul sito chirurgico.",
    alimentazione: "Cibi freddi e morbidi (yogurt, gelato, purè). Evita liquidi caldi.",
    ghiaccio: "Applica ghiaccio sulla guancia 15 minuti ogni ora nelle prime 24-48h.",
    attivita_fisica: "Riposa. Niente sforzi, niente sport, niente di pesante.",
    fumo: "Non fumare oggi e nei prossimi giorni: rallenta seriamente la guarigione.",
    sanguinamento: "Un lieve stillicidio è normale per qualche ora. Mordi una garza pulita con pressione per 30 minuti se sanguina.",
    edema: "Il gonfiore inizierà ad apparire: è normale, raggiungerà il picco a 48-72h.",
  },
  1: { focus: "Primo giorno post-operatorio: il gonfiore può aumentare. Continua la terapia e mantieni la zona pulita.", farmaci: "Continua antibiotico, antinfiammatorio e antidolorifico secondo prescrizione.", igiene_orale: "Spazzolino delicato lontano dal sito chirurgico. Niente sciacqui energici.", alimentazione: "Sempre cibi morbidi e tiepidi/freddi. Idratati bene.", ghiaccio: "Continua il ghiaccio nelle prime 24-48h ad intervalli regolari.", attivita_fisica: "Riposo a casa, evita sforzi e di chinarti in avanti a lungo.", fumo: "Non fumare: ogni sigaretta aumenta il rischio di infezione.", sanguinamento: "Piccole tracce di sangue nella saliva sono normali per le prime 24h.", edema: "Il gonfiore può aumentare oggi e domani: è normale." },
  3: { focus: "Terzo giorno: il gonfiore raggiunge il picco poi inizia a regredire. Verifica che tutto stia migliorando.", farmaci: "Prosegui la terapia come prescritto, non interrompere l'antibiotico.", igiene_orale: "Inizia sciacqui delicati con clorexidina 0,12% dopo i pasti, se prescritto.", alimentazione: "Cibi morbidi, evita ancora cibi caldi/duri/piccanti.", ghiaccio: "Puoi sospendere il ghiaccio. Se gonfiore persiste, calore tiepido può aiutare.", attivita_fisica: "Puoi camminare tranquillamente. Niente sforzi né sport.", fumo: "Continua a non fumare: sei nella fase critica della guarigione.", sanguinamento: "A questo punto non dovresti più sanguinare. In caso contrario, contatta lo studio.", edema: "Il gonfiore dovrebbe iniziare a diminuire da oggi." },
  7: { focus: "Una settimana dopo l'intervento: la guarigione dei tessuti molli è in pieno corso.", farmaci: "Termina l'antibiotico solo se hai completato il ciclo prescritto.", igiene_orale: "Pulizia accurata ma delicata della zona. Sciacqui con clorexidina secondo prescrizione.", alimentazione: "Puoi reintrodurre cibi semi-solidi, evita masticazione diretta sul sito.", ghiaccio: "Non più necessario.", attivita_fisica: "Puoi riprendere attività leggere. Evita sport di contatto.", fumo: "Per favore continua a non fumare: la guarigione completa richiede ancora settimane.", sanguinamento: "Nessun sanguinamento atteso.", edema: "Il gonfiore dovrebbe essere quasi scomparso." },
  14: { focus: "Due settimane: la guarigione superficiale è quasi completa. Sei nella fase di maturazione.", farmaci: "Non assumere farmaci se non prescritti.", igiene_orale: "Routine quotidiana normale, sempre con delicatezza sulla zona.", alimentazione: "Riprendi gradualmente la dieta abituale.", ghiaccio: "Non più necessario.", attivita_fisica: "Puoi riprendere lo sport, senza esagerare nelle prime sessioni.", fumo: "Idealmente continua a non fumare ancora qualche settimana.", sanguinamento: "Non atteso.", edema: "Eventuali residui di gonfiore lieve sono normali." },
  30: { focus: "Un mese dopo l'intervento: la guarigione dei tessuti molli è completa.", farmaci: "Solo come prescritto.", igiene_orale: "Igiene quotidiana accurata. Considera una seduta di igiene professionale tra 2-3 mesi.", alimentazione: "Dieta normale, masticazione bilaterale.", ghiaccio: "Non necessario.", attivita_fisica: "Attività normale, anche sport agonistico.", fumo: "Limitare il fumo migliora la durata degli impianti.", sanguinamento: "Nessuno atteso.", edema: "Nessuno atteso." },
};

function pickStaticBlock(day) {
  const days = Object.keys(STATIC_FALLBACK).map(Number).sort((a, b) => a - b);
  if (day < 0) return STATIC_FALLBACK[0];
  if (day > 30) return STATIC_FALLBACK[30];
  let chosen = 0;
  for (const d of days) if (d <= day) chosen = d;
  return STATIC_FALLBACK[chosen];
}

function TodayInstructions({ init, day, smokingProfile, citisina, patientFirstName, patientToken, onAck }) {
  const ack = init?.daily_ack || null;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Merge: AI briefing wins, otherwise static fallback per day
  const block = pickStaticBlock(day);
  const greeting = init?.greeting || `Ciao ${patientFirstName}, oggi sei al Giorno ${day} dopo il tuo intervento. Ecco le indicazioni da seguire.`;
  const focus = init?.today_focus || block.focus;
  const inst = {
    farmaci: init?.instructions?.farmaci || block.farmaci,
    igiene_orale: init?.instructions?.igiene_orale || block.igiene_orale,
    alimentazione: init?.instructions?.alimentazione || block.alimentazione,
    ghiaccio: init?.instructions?.ghiaccio || block.ghiaccio,
    attivita_fisica: init?.instructions?.attivita_fisica || block.attivita_fisica,
    fumo: init?.instructions?.fumo || block.fumo,
    sanguinamento: init?.instructions?.sanguinamento || block.sanguinamento,
    edema: init?.instructions?.edema || block.edema,
  };

  const submitAck = async () => {
    if (!patientToken) return;
    setBusy(true);
    setErr("");
    try {
      await api.post(`/portal/daily-instructions/ack`, { day }, { headers: { Authorization: `Bearer ${patientToken}` } });
      onAck?.();
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Errore. Riprova.");
    } finally { setBusy(false); }
  };

  const isSmoker = !!smokingProfile?.is_smoker;
  const ackedToday = !!ack?.acknowledged_at;
  const ackedDate = ackedToday ? new Date(ack.acknowledged_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";

  return (
    <div className="elev-card p-6 space-y-5" data-testid="today-instructions">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-brand-400 mb-1 flex items-center gap-1.5">
          <Sparkle size={12} weight="duotone" /> Le istruzioni di oggi
        </div>
        <h3 className="font-heading text-xl font-medium text-[#1C2321]">Giorno {day} post-operatorio</h3>
        <p className="text-[#56635D] mt-2 leading-relaxed">{greeting}</p>
        {focus && <p className="text-sm text-[#1C2321] mt-3 p-3 bg-brand-50 rounded-lg border-l-4 border-brand-700">{focus}</p>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {INSTR_ORDER.map((k) => {
          const text = inst[k];
          if (!text) return null;
          const meta = INSTR_META[k] || { label: k, Icon: Sparkle };
          const Icon = meta.Icon;
          return (
            <div key={k} className="border border-[#E6E8E6] rounded-lg p-3 bg-white" data-testid={`instr-${k}`}>
              <div className="flex items-start gap-2.5">
                <div className="w-8 h-8 shrink-0 rounded-md bg-brand-50 flex items-center justify-center text-brand-700">
                  <Icon size={16} weight="duotone" />
                </div>
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-brand-400">{meta.label}</div>
                  <div className="text-sm text-[#1C2321] mt-0.5 leading-relaxed">{text}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Attenzione al fumo */}
      {isSmoker && (
        <div className="border-l-4 border-[#A84A4A] bg-[#FAEBEB] p-4 rounded-r-lg" data-testid="smoking-warning">
          <div className="flex items-start gap-2.5">
            <div className="w-8 h-8 shrink-0 rounded-md bg-[#A84A4A] text-white flex items-center justify-center">
              <Warning size={16} weight="fill" />
            </div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-[0.18em] text-[#A84A4A] mb-1 font-medium">Attenzione al fumo</div>
              <p className="text-sm text-[#1C2321] leading-relaxed">
                Il fumo <strong>rallenta seriamente la guarigione</strong>, aumenta il rischio di infezione e può ridurre la prognosi del tuo impianto. Ogni sigaretta in meno è un giorno di recupero guadagnato.
              </p>
              {smokingProfile?.cigarettes_per_day > 0 && (
                <p className="text-xs text-[#56635D] mt-2 font-mono">Hai dichiarato {smokingProfile.cigarettes_per_day} sigarette/die.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Protocollo Citisina inline */}
      {isSmoker && citisina?.enabled && (
        <div className="border border-[#8A6D1F]/30 bg-[#FFFAE9] rounded-lg p-4" data-testid="citisina-inline">
          <div className="text-xs uppercase tracking-[0.18em] text-[#8A6D1F] mb-2 flex items-center gap-1.5">
            <Cigarette size={12} weight="duotone" /> {citisina.title || "Protocollo Citisina"}
          </div>
          {citisina.description && <p className="text-sm text-[#56635D] mb-3">{citisina.description}</p>}
          <ol className="space-y-1.5">
            {(citisina.schema_steps || []).map((s, i) => (
              <li key={i} className="text-sm flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-[#8A6D1F] text-white flex items-center justify-center text-[10px] font-mono shrink-0 mt-0.5">{i + 1}</span>
                <span><strong className="font-medium text-[#1C2321]">{s.day_range}</strong> — {s.dose}{s.notes ? <span className="text-[#56635D]"> · {s.notes}</span> : null}</span>
              </li>
            ))}
          </ol>
          <div className="mt-3 text-[11px] text-[#56635D] italic">Seguire sempre le indicazioni del medico o dello staff Mediadent.</div>
        </div>
      )}

      {/* Ack button */}
      <div className="pt-2 border-t border-[#E6E8E6]">
        {ackedToday ? (
          <div className="flex items-center gap-2 text-sm text-brand-700" data-testid="instructions-acked">
            <CheckCircle size={18} weight="fill" /> Istruzioni lette il {ackedDate}. Grazie!
          </div>
        ) : (
          <>
            <button onClick={submitAck} disabled={busy} data-testid="instructions-ack-button"
              className="inline-flex items-center justify-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-5 py-2.5 rounded-lg font-medium text-sm disabled:opacity-60">
              <CheckCircle size={16} weight="duotone" /> {busy ? "Salvataggio…" : "Ho letto le istruzioni di oggi"}
            </button>
            {err && <div className="mt-2 text-sm text-[#A84A4A]">{err}</div>}
          </>
        )}
      </div>
    </div>
  );
}

// ---------- Day 0 Card ----------
function Day0Card({ day0, patientToken, onAck }) {
  const [isSmoker, setIsSmoker] = useState(!!day0?.smoking_profile?.is_smoker);
  const [cigPerDay, setCigPerDay] = useState(day0?.smoking_profile?.cigarettes_per_day || 0);
  const [smoked24h, setSmoked24h] = useState(!!day0?.smoking_profile?.smoked_24h);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!patientToken) {
      setErr("Sessione scaduta. Riapri il link che hai ricevuto.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await api.post(`/portal/day0/acknowledge`, {
        is_smoker: !!isSmoker,
        cigarettes_per_day: isSmoker ? Math.max(0, parseInt(cigPerDay || 0, 10)) : 0,
        smoked_24h: isSmoker ? !!smoked24h : false,
        acknowledged: true,
      }, { headers: { Authorization: `Bearer ${patientToken}` } });
      onAck?.();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        setErr("La sessione è scaduta. Riapri il link che hai ricevuto.");
      } else {
        setErr(formatApiError(e?.response?.data?.detail) || "Errore. Riprova.");
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="elev-card p-6 border-2 border-brand-700/30" data-testid="day0-card">
      <div className="text-xs uppercase tracking-[0.2em] text-brand-700 flex items-center gap-1.5 mb-1">
        <Sparkle size={14} weight="fill" /> Giorno 0 · Inizio percorso
      </div>
      <h2 className="font-heading text-2xl font-medium text-[#1C2321] leading-snug">
        Ciao {day0.patient_first_name}, leggi attentamente prima di iniziare
      </h2>
      <p className="text-sm text-[#56635D] mt-2">
        {day0.intervention_type ? <>Intervento: <span className="text-[#1C2321] font-medium">{day0.intervention_type}</span>{day0.intervention_date ? ` · ${day0.intervention_date}` : ""}</> : "Inizia il tuo percorso di guarigione."}
      </p>

      {day0.post_op_instructions && (
        <div className="mt-5">
          <div className="text-xs uppercase tracking-[0.18em] text-brand-400 mb-2">Istruzioni post-operatorie</div>
          <p className="text-sm text-[#1C2321] whitespace-pre-line leading-relaxed">{day0.post_op_instructions}</p>
        </div>
      )}

      {(day0.medications || []).length > 0 && (
        <div className="mt-5" data-testid="day0-meds">
          <div className="text-xs uppercase tracking-[0.18em] text-brand-400 mb-2">Farmaci prescritti</div>
          <ul className="space-y-2">
            {day0.medications.map((m, i) => (
              <li key={i} className="text-sm text-[#1C2321] flex items-start gap-2">
                <Prescription size={14} weight="duotone" className="mt-0.5 text-brand-700 shrink-0" />
                <span><strong className="font-medium">{m.name || "—"}</strong> {m.dosage || ""} {m.frequency || ""} {m.duration ? `per ${m.duration}` : ""}{m.notes ? ` · ${m.notes}` : ""}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(day0.warnings || []).length > 0 && (
        <div className="mt-5" data-testid="day0-warnings">
          <div className="text-xs uppercase tracking-[0.18em] text-[#A84A4A] mb-2 flex items-center gap-1.5"><Warning size={12} weight="duotone" /> Avvertenze importanti</div>
          <ul className="space-y-1.5">
            {day0.warnings.map((w, i) => (
              <li key={i} className="text-sm text-[#1C2321] flex items-start gap-2">
                <span className="text-[#A84A4A] mt-0.5">•</span>
                <span>{w}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Smoking questionnaire */}
      <div className="mt-6 pt-5 border-t border-[#E6E8E6]" data-testid="day0-smoking-block">
        <div className="text-xs uppercase tracking-[0.18em] text-brand-400 mb-2 flex items-center gap-1.5"><Cigarette size={12} weight="duotone" /> Profilo fumatore</div>
        <p className="text-sm text-[#56635D] mb-3">Il fumo rallenta seriamente la guarigione. Aiutaci a personalizzare il tuo percorso.</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
          <button type="button" onClick={() => setIsSmoker(false)} data-testid="day0-smoker-no"
            className={`px-4 py-2.5 rounded-lg text-sm border transition-colors ${!isSmoker ? "bg-brand-700 text-white border-brand-700" : "bg-white border-[#E6E8E6] text-[#1C2321] hover:border-brand-300"}`}>
            Non fumo
          </button>
          <button type="button" onClick={() => setIsSmoker(true)} data-testid="day0-smoker-yes"
            className={`px-4 py-2.5 rounded-lg text-sm border transition-colors ${isSmoker ? "bg-brand-700 text-white border-brand-700" : "bg-white border-[#E6E8E6] text-[#1C2321] hover:border-brand-300"}`}>
            Sono fumatore
          </button>
        </div>

        {isSmoker && (
          <div className="space-y-3" data-testid="day0-smoking-details">
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] text-[#56635D] mb-2">
                Sigarette al giorno: <span className="font-mono text-brand-700">{cigPerDay}</span>
              </label>
              <input type="range" min={0} max={40} value={cigPerDay}
                onChange={(e) => setCigPerDay(parseInt(e.target.value, 10))}
                data-testid="day0-cig-slider"
                className="w-full accent-brand-700" />
              <div className="flex justify-between text-[10px] text-[#56635D] mt-1 px-0.5"><span>0</span><span>10</span><span>20</span><span>30</span><span>40</span></div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setSmoked24h(false)} data-testid="day0-24h-no"
                className={`px-3 py-2 rounded-lg text-sm border transition-colors ${!smoked24h ? "bg-brand-700 text-white border-brand-700" : "bg-white border-[#E6E8E6] text-[#1C2321]"}`}>
                Non ho fumato in 24h
              </button>
              <button type="button" onClick={() => setSmoked24h(true)} data-testid="day0-24h-yes"
                className={`px-3 py-2 rounded-lg text-sm border transition-colors ${smoked24h ? "bg-brand-700 text-white border-brand-700" : "bg-white border-[#E6E8E6] text-[#1C2321]"}`}>
                Ho fumato nelle ultime 24h
              </button>
            </div>

            {day0.citisina_protocol?.enabled && (
              <div className="mt-3 p-3 rounded-lg bg-brand-50 border border-brand-200">
                <div className="text-xs uppercase tracking-[0.18em] text-brand-700 mb-1">Supporto sospensione fumo</div>
                <div className="text-sm text-[#1C2321]">Lo studio ti propone il <strong className="font-medium">{day0.citisina_protocol.title}</strong>. Lo vedrai nella prossima sezione e nei follow-up.</div>
              </div>
            )}
          </div>
        )}
      </div>

      {err && <div className="mt-4 text-sm text-[#A84A4A] bg-[#A84A4A]/10 rounded-lg px-3 py-2">{err}</div>}

      <button onClick={submit} disabled={busy} data-testid="day0-acknowledge-button"
        className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-6 py-3 rounded-lg font-medium disabled:opacity-60">
        <CheckCircle size={18} weight="duotone" /> {busy ? "Salvataggio…" : "Ho letto e compreso"}
      </button>
    </div>
  );
}

// ---------- Citisina protocol card ----------
function CitisinaProtocolCard({ protocol }) {
  if (!protocol) return null;
  return (
    <div className="elev-card p-6 bg-[#FFFAE9] border-[#8A6D1F]/20" data-testid="citisina-protocol-card">
      <div className="text-xs uppercase tracking-[0.18em] text-[#8A6D1F] mb-1 flex items-center gap-1.5">
        <Cigarette size={12} weight="duotone" /> Supporto alla sospensione del fumo
      </div>
      <h3 className="font-heading text-xl font-medium text-[#1C2321]">{protocol.title}</h3>
      {protocol.description && <p className="text-sm text-[#56635D] mt-2">{protocol.description}</p>}
      <ol className="mt-4 space-y-2">
        {(protocol.schema_steps || []).map((s, i) => (
          <li key={i} className="flex items-start gap-3" data-testid={`citisina-step-${i}`}>
            <div className="w-7 h-7 rounded-full bg-[#8A6D1F] text-white flex items-center justify-center text-xs font-mono shrink-0">{i + 1}</div>
            <div className="text-sm">
              <div className="font-medium text-[#1C2321]">{s.day_range || ""} — {s.dose || ""}</div>
              {s.notes && <div className="text-[#56635D] text-xs mt-0.5">{s.notes}</div>}
            </div>
          </li>
        ))}
      </ol>
      <div className="mt-4 text-[11px] text-[#56635D]">Protocollo a titolo informativo. Segui sempre le indicazioni del tuo medico.</div>
    </div>
  );
}

// ---------- Subcomponents ----------

function Timeline({ day }) {
  return (
    <div className="elev-card p-6" data-testid="assistant-timeline">
      <div className="text-xs uppercase tracking-[0.2em] text-brand-400 mb-1">Sezione 6</div>
      <h3 className="font-heading text-xl font-medium text-[#1C2321] mb-4">La tua timeline di guarigione</h3>
      <div className="grid grid-cols-5 gap-2 sm:gap-3">
        {TIMELINE_DAYS.map((d, i) => {
          const reached = day >= d;
          const current = day === d || (i > 0 && day < d && day >= TIMELINE_DAYS[i - 1] + 1) || (i === 0 && day < TIMELINE_DAYS[0]);
          return (
            <div key={d} className="text-center" data-testid={`timeline-day-${d}`}>
              <div className={`mx-auto w-10 h-10 rounded-full flex items-center justify-center font-heading font-medium ${
                reached ? "bg-brand-700 text-white" : current ? "bg-brand-200 text-brand-700" : "bg-[#F0F0EE] text-[#56635D]"
              }`}>
                {d}
              </div>
              <div className="text-[10px] uppercase tracking-[0.15em] mt-1.5 text-[#56635D]">Giorno</div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 h-1.5 bg-[#F0F0EE] rounded-full overflow-hidden">
        <div className="h-full bg-brand-700 transition-all" style={{ width: `${Math.min(100, (day / 30) * 100)}%` }} />
      </div>
    </div>
  );
}

function SliderRow({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-[#56635D] mb-2">{label}</label>
      <input type="range" min={0} max={10} value={value} onChange={(e) => onChange(parseInt(e.target.value, 10))} data-testid={testId} className="w-full accent-brand-700" />
      <div className="flex justify-between text-[10px] text-[#56635D] mt-1 px-0.5"><span>0</span><span>5</span><span>10</span></div>
    </div>
  );
}

function RadioRow({ label, value, options, onChange, testId }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-[#56635D] mb-2">{label}</label>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {options.map((o) => (
          <button
            type="button"
            key={o.v}
            onClick={() => onChange(o.v)}
            data-testid={`${testId}-${o.v}`}
            className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
              value === o.v ? "bg-brand-700 text-white border-brand-700" : "bg-white text-[#1C2321] border-[#E6E8E6] hover:border-brand-300"
            }`}
          >
            {o.l}
          </button>
        ))}
      </div>
    </div>
  );
}

function Toggle({ label, checked, onChange, testId }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      data-testid={testId}
      className={`text-left px-3 py-2.5 rounded-lg border transition-colors ${
        checked ? "bg-brand-700 text-white border-brand-700" : "bg-white text-[#1C2321] border-[#E6E8E6] hover:border-brand-300"
      }`}
    >
      <div className="text-[10px] uppercase tracking-[0.18em] opacity-70">{checked ? "Sì" : "No"}</div>
      <div className="text-sm font-medium">{label}</div>
    </button>
  );
}

function RiskCard({ risk }) {
  const r = RISK_META[risk] || RISK_META.yellow;
  const Icon = r.Icon;
  return (
    <div className={`rounded-xl border-2 p-5 ${r.color}`} data-testid="assistant-risk-card">
      <div className="flex items-center gap-3">
        <Icon size={28} weight="duotone" />
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] opacity-70">Valutazione automatica</div>
          <div className="font-heading text-2xl font-medium mt-0.5">{r.label}</div>
        </div>
      </div>
    </div>
  );
}
